import { useCallback, useEffect, useRef } from 'react'
import Editor, { type Monaco, type OnMount } from '@monaco-editor/react'
import { useTranslation } from 'react-i18next'
import { useEditorStore } from '@/store'
import type { DataFormat } from '@/types'

const formatToLanguage: Record<DataFormat, string> = {
  json: 'json',
  yaml: 'yaml',
  xml: 'xml',
  toml: 'plaintext',
  edi: 'plaintext',
  ofx: 'xml',
  cnab: 'plaintext',
  auto: 'plaintext',
}

export default function CodeEditor() {
  const { t } = useTranslation()
  const { content, format, setContent, parseAndRender, theme, isLoading, error } = useEditorStore()
  const editorRef = useRef<Parameters<OnMount>[0] | null>(null)
  const monacoRef = useRef<Monaco | null>(null)
  const debounceRef = useRef<NodeJS.Timeout | null>(null)

  const handleEditorMount: OnMount = (editor, monaco) => {
    editorRef.current = editor
    monacoRef.current = monaco

    // Configure JSON validation
    monaco.languages.json.jsonDefaults.setDiagnosticsOptions({
      validate: true,
      schemaValidation: 'error',
      allowComments: true,
      trailingCommas: 'warning',
    })

    // Configure editor options
    editor.updateOptions({
      minimap: { enabled: false },
      fontSize: 13,
      fontFamily: 'JetBrains Mono, SF Mono, Monaco, Menlo, monospace',
      fontLigatures: true,
      lineNumbers: 'on',
      renderLineHighlight: 'line',
      scrollBeyondLastLine: false,
      wordWrap: 'on',
      tabSize: 2,
      automaticLayout: true,
      padding: { top: 16, bottom: 16 },
    })
  }

  const handleChange = useCallback(
    (value: string | undefined) => {
      setContent(value || '')

      // Debounce parsing
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }

      debounceRef.current = setTimeout(() => {
        parseAndRender()
      }, 500)
    },
    [setContent, parseAndRender]
  )

  // Update editor theme when app theme changes
  useEffect(() => {
    if (monacoRef.current) {
      const isDark = theme === 'dark' || 
        (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches)
      monacoRef.current.editor.setTheme(isDark ? 'vs-dark' : 'vs')
    }
  }, [theme])

  // Clean up debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
    }
  }, [])

  const editorTheme = theme === 'dark' || 
    (theme === 'system' && typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches)
    ? 'vs-dark'
    : 'vs'

  return (
    <div className="h-full flex flex-col bg-panel-light dark:bg-panel-dark min-w-[260px]">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2 border-b bg-white dark:bg-slate-900">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
            {t('nav.editor')}
          </span>
          {isLoading && (
            <span className="text-xs text-primary-500 animate-pulse">
              {t('status.parsing')}
            </span>
          )}
        </div>
        
        {error && (
          <span className="text-xs text-red-500 truncate max-w-[200px]" title={error}>
            {error}
          </span>
        )}
      </div>

      {/* Editor */}
      <div className="flex-1 overflow-hidden">
        <Editor
          height="100%"
          language={formatToLanguage[format]}
          value={content}
          theme={editorTheme}
          onChange={handleChange}
          onMount={handleEditorMount}
          loading={
            <div className="flex items-center justify-center h-full">
              <div className="text-slate-400">{t('status.rendering')}</div>
            </div>
          }
          options={{
            readOnly: false,
            minimap: { enabled: false },
          }}
        />
      </div>
    </div>
  )
}
